import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { FileDown } from "lucide-react";
import { toast } from "sonner";

export default function CompliancePage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/compliance").then((r) => setData(r.data));
  }, [user?.mode]);

  const exportAudit = async () => {
    try {
      const { data } = await api.get("/compliance/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `dndi_audit_${data.mode}_${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit exporté");
    } catch (err) {
      toast.error("Erreur export");
    }
  };

  if (!data) return <div className="text-white/50">Chargement…</div>;

  const rgpd = data.controls.filter((c) => c.framework === "RGPD");
  const hipaa = data.controls.filter((c) => c.framework === "HIPAA");
  const rgpdCov = Math.round((rgpd.filter((c) => c.status === "pass").length / rgpd.length) * 100);
  const hipaaCov = Math.round((hipaa.filter((c) => c.status === "pass").length / hipaa.length) * 100);

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
            {t("compliance")}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Tableau de bord conformité</h1>
        </div>
        <Button onClick={exportAudit} data-testid="export-audit-btn"
                className="bg-white/10 hover:bg-white/20 text-white">
          <FileDown className="h-4 w-4" /> {t("export_audit")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white/[0.02] border-white/10 rounded-sm p-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
            {t("coverage")} globale
          </div>
          <div className="text-4xl font-semibold tracking-tight mt-2">{data.coverage}%</div>
          <Progress value={data.coverage} className="mt-3 h-1 bg-white/10" />
          <div className="mt-2 text-[10px] font-mono text-white/50">
            {data.passed}/{data.total} contrôles
          </div>
        </Card>

        <Card className="bg-white/[0.02] border-white/10 rounded-sm p-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">RGPD</div>
          <div className="text-4xl font-semibold tracking-tight mt-2">{rgpdCov}%</div>
          <Progress value={rgpdCov} className="mt-3 h-1 bg-white/10" />
        </Card>

        <Card className="bg-white/[0.02] border-white/10 rounded-sm p-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">HIPAA</div>
          <div className="text-4xl font-semibold tracking-tight mt-2">{hipaaCov}%</div>
          <Progress value={hipaaCov} className="mt-3 h-1 bg-white/10" />
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ControlsTable title="RGPD" controls={rgpd} />
        <ControlsTable title="HIPAA" controls={hipaa} />
      </div>
    </div>
  );
}

function ControlsTable({ title, controls }) {
  return (
    <Card className="bg-white/[0.02] border-white/10 rounded-sm p-4"
          data-testid={`controls-${title.toLowerCase()}`}>
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-3">{title}</div>
      <div className="space-y-2">
        {controls.map((c) => (
          <div key={c.code} className="border border-white/10 rounded-sm p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-cyan-300">{c.code}</span>
                <span className="text-sm">{c.label}</span>
              </div>
              <StatusBadge status={c.status}>{c.status}</StatusBadge>
            </div>
            <div className="mt-1 text-[11px] text-white/50">{c.detail}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}
